import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DOMAINS, getDomainById } from '../../../shared/constants/domains';

/**
 * Command Drill home screen with category tiles and accuracy heat coloring.
 */
export default function DrillHome({ questions }) {
  const navigate = useNavigate();

  // Group questions by category
  const categories = useMemo(() => {
    const map = {};
    for (const q of questions) {
      if (!map[q.category]) {
        map[q.category] = { name: q.category, domain: q.domain, questions: [], correct: 0, attempted: 0 };
      }
      map[q.category].questions.push(q);
      map[q.category].attempted += q.stats.times_attempted;
      map[q.category].correct += q.stats.times_correct;
    }
    return Object.values(map).sort((a, b) => b.questions.length - a.questions.length);
  }, [questions]);

  const totalQuestions = questions.length;
  const totalAttempted = questions.reduce((s, q) => s + q.stats.times_attempted, 0);
  const totalCorrect = questions.reduce((s, q) => s + q.stats.times_correct, 0);

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-1" style={{ color: '#00ff41' }}>Command Syntax Drill</h1>
        <p className="text-gray-500 text-sm font-mono">IOS-XE command recall under pressure</p>
      </div>

      {/* Quick start */}
      <div className="bg-gray-900 rounded-xl border border-gray-700 p-6 mb-6 text-center">
        <div className="text-3xl font-bold text-white mb-1">{totalQuestions}</div>
        <p className="text-gray-400 mb-4 text-sm">questions in the bank</p>
        <div className="flex justify-center gap-3 flex-wrap">
          <button
            onClick={() => navigate('/drill/session/builder')}
            className="px-6 py-3 bg-green-700 text-green-100 rounded-lg hover:bg-green-600 font-mono"
          >
            Command Builder
          </button>
          <button
            onClick={() => navigate('/drill/session/speed')}
            className="px-6 py-3 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 font-mono"
          >
            Speed Round
          </button>
          <button
            onClick={() => navigate('/drill/session/syntaxfix')}
            className="px-6 py-3 bg-red-900/50 text-red-300 rounded-lg hover:bg-red-900 font-mono"
          >
            Syntax Fix
          </button>
        </div>
      </div>

      {/* Overall stats */}
      {totalAttempted > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-8">
          <StatBox label="Attempted" value={totalAttempted} />
          <StatBox label="Correct" value={totalCorrect} />
          <StatBox label="Accuracy" value={`${totalAttempted > 0 ? Math.round((totalCorrect / totalAttempted) * 100) : 0}%`} />
        </div>
      )}

      {/* Category tiles */}
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Categories</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        {categories.map(cat => {
          const accuracy = cat.attempted > 0 ? cat.correct / cat.attempted : null;
          const domain = getDomainById(cat.domain);
          const heatColor = accuracy === null ? '#333' :
            accuracy >= 0.8 ? '#166534' :
            accuracy >= 0.5 ? '#854d0e' : '#991b1b';

          return (
            <button
              key={cat.name}
              onClick={() => navigate(`/drill/session/builder?category=${encodeURIComponent(cat.name)}`)}
              className="bg-gray-900 border border-gray-700 rounded-lg p-4 text-left hover:border-gray-500 transition-colors"
              style={{ borderLeftColor: domain?.color, borderLeftWidth: '3px' }}
            >
              <div className="font-medium text-white text-sm">{cat.name}</div>
              <div className="text-xs text-gray-500 mt-1">{cat.questions.length} questions</div>
              {accuracy !== null && (
                <div className="mt-2">
                  <div className="w-full bg-gray-800 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full"
                      style={{ width: `${accuracy * 100}%`, backgroundColor: heatColor }}
                    />
                  </div>
                  <div className="text-xs mt-1" style={{ color: heatColor === '#333' ? '#666' : heatColor }}>
                    {Math.round(accuracy * 100)}%
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Links */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <Link to="/drill/browse" className="hover:text-gray-300 font-mono">Browse all questions →</Link>
      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-gray-900 rounded-lg p-3 text-center border border-gray-700">
      <div className="text-xl font-bold" style={{ color: '#00ff41' }}>{value}</div>
      <div className="text-xs text-gray-500 mt-1 font-mono">{label}</div>
    </div>
  );
}
